import React, { useCallback, useMemo, useState } from "react";

import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ErrorIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import { Stack } from "@mui/material";

import useGetPortfolioPlanById from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlanById";
import {
  ClientId,
  FartherAccountId,
  PortfolioPlanId,
} from "@fartherfinance/frontend/api/Types";
import { useTheme } from "@fartherfinance/frontend/theme/ThemeProvider";

import Button from "../../../MUI/Button/Button";
import { SHARED_ACCOUNT_TOOLTIP_TEXT } from "../const";
import { InvestmentsPath } from "@src/config/routing/RouterPaths";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import { typedKeys } from "@src/multiCustodian/utils/arrayHelpers";
import Checkbox from "@src/sharedComponents/Checkbox/Checkbox";
import LinkButton from "@src/sharedComponents/LinkButton/LinkButton";
import RadioButtons from "@src/sharedComponents/Radio/RadioButtons";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import Tooltip from "@src/sharedComponents/Tooltip/Tooltip";
import Text from "@src/sharedComponents/Typography/Text";
import { pluralize } from "@src/utils/pluralize";
import toUSD from "@src/utils/toUSD";

import TradingModalV2 from "./modals/TradingModalV2";

import styles from "./CreateTradingGroupTableV2.module.css";

export type SelectableAccountRowV2Props = {
  accountName: string;
  isJointAccount: boolean;
  fartherAccountId: FartherAccountId;
  isDisabled: boolean;
  disabledReason: string | null;
  portfolioPlanId: PortfolioPlanId | null;
  clientId: ClientId;
  accountBalance: number | null;
};

type ActualProps = SelectableAccountRowV2Props & {
  selectAccountCheckbox: (custodianAccountNumber: FartherAccountId) => void;
  isCheckboxChecked: boolean;
};

type RadioBtnValue =
  | "Tax-aware coordination across accounts (asset location)"
  | "Invest the accounts individually";

const SelectableAccountRowV2 = ({
  fartherAccountId,
  accountName,
  isJointAccount,
  isCheckboxChecked,
  selectAccountCheckbox,
  isDisabled,
  disabledReason,
  portfolioPlanId,
  clientId,
  accountBalance,
}: ActualProps) => {
  const auth = useRequestAuth();
  const { data: portfolioPlan, isLoading } = useGetPortfolioPlanById(
    portfolioPlanId,
    {},
    auth
  );

  const portfolioName = portfolioPlan?.data.plan.name;

  return (
    <div className={styles.optionDiv}>
      <div className={styles.checkboxDiv}>
        <Checkbox
          label={
            <div className={styles.checkboxLabel}>
              <div className={styles.checkboxLabelText}>{`${accountName}`}</div>

              {isDisabled && disabledReason !== null && (
                <Tooltip tooltipText={disabledReason} placement="top">
                  <InfoOutlinedIcon className={styles.infoIconDisabled} />
                </Tooltip>
              )}
            </div>
          }
          checked={isCheckboxChecked}
          onChange={() => selectAccountCheckbox(fartherAccountId)}
          disabled={isDisabled}
        />

        {isJointAccount && (
          <Tooltip tooltipText={SHARED_ACCOUNT_TOOLTIP_TEXT} placement={"top"}>
            <PeopleAltIcon className={styles.sharedIcon} />
          </Tooltip>
        )}
      </div>

      <Stack direction={"row"} flex={1} width={"100%"} justifyContent={"right"}>
        {portfolioPlanId ? (
          <React.Fragment>
            {isLoading || portfolioName === undefined ? (
              <Skeleton width={"full"} />
            ) : (
              <Text
                color="textSubtle"
                style={{ display: "flex", flexDirection: "row" }}
              >
                Assigned to
                <LinkButton
                  className={styles.customLink}
                  isCustom={true}
                  to={`/client/${clientId}/${InvestmentsPath}/PortfoliosV2/${portfolioPlanId}/Accounts`}
                  buttonType="secondary"
                  variant="text"
                  text={
                    <>
                      {`${portfolioName}`}
                      <ChevronRightRoundedIcon
                        className={styles.chevronRight}
                      />
                    </>
                  }
                />
              </Text>
            )}
          </React.Fragment>
        ) : (
          <Text>
            Balance{" "}
            <span className={styles.balanceText} style={{ paddingRight: 15 }}>
              {accountBalance === null ? "N/A" : toUSD(accountBalance)}
            </span>
          </Text>
        )}
      </Stack>
    </div>
  );
};

type CreateTradingGroupV2Props = {
  data: SelectableAccountRowV2Props[];
  onApply: (
    selectedAccounts: FartherAccountId[],
    isIndividual: boolean
  ) => void;
};

const CreateTradingGroupV2 = (props: CreateTradingGroupV2Props) => {
  const {
    color: { $textSubtle },
  } = useTheme();

  const [checkBoxState, seCheckboxState] = useState<
    Record<FartherAccountId, boolean>
  >({});

  const [radioBtnValue, setRadioBtnValue] = useState<RadioBtnValue>(
    "Tax-aware coordination across accounts (asset location)"
  );

  const selectedAccounts = useMemo(
    () => typedKeys(checkBoxState).filter((key) => checkBoxState[key]),
    [checkBoxState]
  );

  const [addAccountModal, setAddAccountModal] = useState(false);

  const onApplyCallback = useCallback(() => {
    props.onApply(
      selectedAccounts,
      radioBtnValue === "Invest the accounts individually"
    );
    trackEvent({
      name: "Client Click Confirm Assign Model to Accounts",
    });
    setAddAccountModal(false);
  }, [props, radioBtnValue, selectedAccounts]);

  const setModalState = useCallback(
    (isOpen: boolean) => () => {
      setAddAccountModal(isOpen);
    },
    []
  );

  const accountsAvailable: number = useMemo(
    () => props.data.filter((account) => !account.isDisabled).length,
    [props.data]
  );

  return (
    <div className={styles.shell}>
      <div className={styles.container}>
        <div className={styles.listHeaderDiv}>
          <div className={styles.flexDiv}>
            {props.data.length === 0 ? (
              <p className={styles.headerText2}>{"No Accounts Available"}</p>
            ) : (
              <Stack direction={"row"} gap={2}>
                <p className={styles.headerText1}>Assign model to accounts</p>
                <Text
                  variant="body"
                  color="textSubtle"
                  style={{ alignContent: "center" }}
                >
                  {`${pluralize(accountsAvailable, "Account", true)} available`}
                </Text>
              </Stack>
            )}
          </div>

          <Button
            buttonType={"primary"}
            variant={"contained"}
            text={"Save"}
            disabled={selectedAccounts.length <= 0}
            onClick={setModalState(true)}
            sx={{ height: 30 }}
          />
          <TradingModalV2
            isCurrentlyTrading={false}
            isOpen={addAccountModal}
            closeModal={setModalState(false)}
            onConfirm={onApplyCallback}
            isMutating={false}
          />
        </div>
        {selectedAccounts.length >= 2 ? (
          <div className={styles.radioButtonsDiv}>
            <RadioButtons
              label={
                "How should we invest your funds across the accounts you've selected?"
              }
              values={[
                "Tax-aware coordination across accounts (asset location)",
                "Invest the accounts individually",
              ]}
              onChange={(newValue: RadioBtnValue) => setRadioBtnValue(newValue)}
              value={radioBtnValue}
              isValid={undefined}
              labelColor={$textSubtle}
              row={false}
            />
          </div>
        ) : null}
        <div className={styles.optionsDiv}>
          {props.data.map((account) => (
            <SelectableAccountRowV2
              accountBalance={account.accountBalance}
              clientId={account.clientId}
              portfolioPlanId={account.portfolioPlanId}
              isDisabled={account.isDisabled}
              disabledReason={null}
              fartherAccountId={account.fartherAccountId}
              key={account.fartherAccountId}
              accountName={account.accountName}
              isJointAccount={account.isJointAccount}
              isCheckboxChecked={Boolean(
                checkBoxState[account.fartherAccountId]
              )}
              selectAccountCheckbox={(custodianAccountNumber) => {
                seCheckboxState((prevState) => ({
                  ...prevState,
                  [custodianAccountNumber]: !prevState[custodianAccountNumber],
                }));
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export const LoadingCreateTradingTable = () => {
  return (
    <div className={styles.shell}>
      <div className={styles.container}>
        <div className={styles.listHeaderDiv}>
          <div className={styles.flexDiv}>
            <Skeleton width={200} />
          </div>
        </div>

        {new Array(3).fill(null).map((_, i) => (
          <div className={styles.optionDiv} key={i}>
            <div className={styles.checkboxDiv}>
              <Checkbox
                label={
                  <div className={styles.checkboxLabel}>
                    <div className={styles.checkboxLabelText}>
                      <Skeleton width={200} />
                    </div>
                  </div>
                }
                checked={false}
                onChange={() => undefined}
                disabled={true}
              />
            </div>
            <Skeleton width={200} />
          </div>
        ))}
      </div>
    </div>
  );
};

export const ErrorCreateTradingTable = () => {
  return (
    <div className={styles.shell}>
      <div className={styles.container}>
        <div className={styles.listHeaderDiv}>
          <div className={styles.flexDiv}>
            <p className={styles.headerText2}>{"No Accounts Available"}</p>
          </div>
        </div>
        <div
          style={{
            height: "200px",
            justifyContent: "center",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <ErrorIcon color={"info"} fontSize="small" />
          <Text variant="body" color="textSubtle">
            Error loading table
          </Text>
        </div>
      </div>
    </div>
  );
};

export default CreateTradingGroupV2;
